import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles, Loader2, Calendar, Building2, ScrollText, Trash2, Plus, Save, BookOpen, X } from "lucide-react";
import { listCompanies, listResolutionTypes, draftResolutionsBatch, saveResolutionsBatch } from "@/lib/api";
import { toast } from "sonner";
import Combobox from "@/components/Combobox";
import SpecificFieldsPanel from "@/components/SpecificFieldsPanel";

const MAX_ITEMS = 5;

const _newRid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `r${Math.random().toString(36).slice(2)}`);
const _newItem = (key = "") => ({
  _rid: _newRid(),
  resolution_type_key: key,
  custom_title: "",
  custom_context: "",
  is_special_resolution: false,
  include_shareholder_resolution: false,
  specific_fields: {},
});

export default function GenerateResolution() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  const today = new Date();
  const defaultMeeting = new Date(today.getTime() + 8 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  // --- Step 1 form ---
  const [form, setForm] = useState({
    company_id: "",
    signing_director_dins: [],
    meeting_date: defaultMeeting,
    meeting_time: "11:00 AM",
    meeting_place: "",
    meeting_type: "board",
    include_notice: true,
    include_minutes: true,
    use_style_library: true,
  });
  const [items, setItems] = useState([_newItem("")]);

  // --- Step 2 preview ---
  const [draftBatch, setDraftBatch] = useState(null);
  const [editedDrafts, setEditedDrafts] = useState([]);
  const [activeDraftIdx, setActiveDraftIdx] = useState(0);
  const [activeTab, setActiveTab] = useState("resolution");

  useEffect(() => {
    Promise.all([listCompanies(), listResolutionTypes()])
      .then(([c, t]) => {
        const cs = c.data || [];
        const ts = t.data || [];
        setCompanies(cs);
        setTypes(ts);
        const preCompany = searchParams.get("company");
        const preType = searchParams.get("type");
        setForm((f) => ({ ...f, company_id: preCompany || (cs[0]?.id ?? "") }));
        if (preType) {
          setItems([{ resolution_type_key: preType, custom_title: "", custom_context: "", is_special_resolution: false, include_shareholder_resolution: false, specific_fields: {} }]);
        } else {
          setItems([{ resolution_type_key: ts[0]?.key ?? "", custom_title: "", custom_context: "", is_special_resolution: false, include_shareholder_resolution: false, specific_fields: {} }]);
        }
      })
      .finally(() => setLoading(false));
  }, [searchParams]);

  const company = useMemo(() => companies.find((c) => c.id === form.company_id), [companies, form.company_id]);

  useEffect(() => {
    if (company && !form.meeting_place) {
      setForm((f) => ({ ...f, meeting_place: company.registered_address || "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  // Auto-derive specific flags per item when its type changes
  const updateItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, ...patch };
      if (patch.resolution_type_key !== undefined) {
        const rt = types.find((t) => t.key === patch.resolution_type_key);
        const kind = rt?.resolution_kind || "board";
        next.is_special_resolution = kind.endsWith("+special");
        next.include_shareholder_resolution = /\+(ordinary|special)$/.test(kind);
        // Reset specific_fields when type changes — the field set is different.
        next.specific_fields = {};
        // If any item needs shareholder, bump meeting type to EGM
        if (next.include_shareholder_resolution && form.meeting_type === "board") {
          setForm((f) => ({ ...f, meeting_type: "egm" }));
        }
      }
      return next;
    }));
  };

  const addItem = () => {
    if (items.length >= MAX_ITEMS) {
      toast.error(`Max ${MAX_ITEMS} resolutions per meeting`);
      return;
    }
    setItems((prev) => [...prev, _newItem(types[0]?.key ?? "")]);
  };

  const removeItem = (idx) => {
    if (items.length === 1) {
      toast.error("Need at least one resolution");
      return;
    }
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const toggleDirector = (din) =>
    setForm((f) => ({
      ...f,
      signing_director_dins: f.signing_director_dins.includes(din)
        ? f.signing_director_dins.filter((d) => d !== din)
        : [...f.signing_director_dins, din],
    }));

  const generate = async () => {
    if (!form.company_id) { toast.error("Select a company"); return; }
    if (items.some((it) => !it.resolution_type_key)) { toast.error("Pick a type for every resolution"); return; }
    if (form.signing_director_dins.length === 0) { toast.error("Select at least one signing director"); return; }

    setGenerating(true);
    try {
      const payload = {
        company_id: form.company_id,
        items: items.map((it) => {
          // Coerce number-typed specific_fields from string → number so the
          // backend Pydantic model receives the correct types.
          const sf = { ...(it.specific_fields || {}) };
          const rt = types.find((t) => t.key === it.resolution_type_key);
          for (const f of rt?.specific_fields || []) {
            if (f.type === "number" && sf[f.key] !== undefined && sf[f.key] !== "") {
              const n = Number(sf[f.key]);
              if (!Number.isNaN(n)) sf[f.key] = n;
            }
          }
          return {
            resolution_type_key: it.resolution_type_key,
            custom_title: it.custom_title || null,
            custom_context: it.custom_context || null,
            is_special_resolution: it.is_special_resolution,
            include_shareholder_resolution: it.include_shareholder_resolution,
            specific_fields: sf,
          };
        }),
        signing_director_dins: form.signing_director_dins,
        meeting_date: form.meeting_date,
        meeting_time: form.meeting_time,
        meeting_place: form.meeting_place,
        meeting_type: form.meeting_type,
        include_notice: form.include_notice,
        include_minutes: form.include_minutes,
        use_style_library: form.use_style_library,
      };
      const res = await draftResolutionsBatch(payload);
      setDraftBatch(res.data);
      // Mirror LLM drafts + carry forward each item's specific_fields so we
      // can pass them through to save-batch (used for MGT-1 auto-link).
      setEditedDrafts(res.data.drafts.map((d, idx) => ({
        ...d,
        _specific_fields: payload.items[idx]?.specific_fields || {},
      })));
      setActiveDraftIdx(0);
      setActiveTab("resolution");
      toast.success(
        `${res.data.drafts.length} draft${res.data.drafts.length > 1 ? "s" : ""} ready` +
        (res.data.used_style_samples > 0 ? ` · used ${res.data.used_style_samples} style sample(s)` : "")
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const res = await saveResolutionsBatch({
        company_id: draftBatch.company_id,
        meeting_date: draftBatch.meeting_date,
        meeting_time: draftBatch.meeting_time,
        meeting_place: draftBatch.meeting_place,
        meeting_type: draftBatch.meeting_type,
        signing_director_dins: draftBatch.signing_director_dins,
        drafts: editedDrafts.map((d) => ({
          resolution_type_key: d.resolution_type_key,
          resolution_title: d.resolution_title,
          resolution_body: d.resolution_body,
          notice_body: d.notice_body,
          minutes_body: d.minutes_body,
          shareholder_resolution_body: d.shareholder_resolution_body,
          is_special_resolution: d.is_special_resolution,
          applicable_sections: d.applicable_sections,
          roc_forms: d.roc_forms,
          statutory_timeline: d.statutory_timeline,
          procedure_followed: d.procedure_followed,
          notice_required: d.notice_required,
          notice_date: d.notice_date,
          specific_fields: d._specific_fields || undefined,
        })),
      });
      const saved = res.data || [];
      toast.success(`Saved ${saved.length} resolution${saved.length > 1 ? "s" : ""}`);
      if (saved.length === 1) navigate(`/resolutions/${saved[0].id}`);
      else if (saved[0]?.meeting_id) navigate(`/meetings/${saved[0].meeting_id}`);
      else navigate("/resolutions");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const updateDraftField = (idx, field, value) => {
    setEditedDrafts((prev) => prev.map((d, i) => i === idx ? { ...d, [field]: value } : d));
  };

  // Group types — memoized so Combobox options below don't reallocate every render.
  // Must run before any early returns (rules-of-hooks).
  const grouped = useMemo(
    () => types.reduce((acc, t) => {
      (acc[t.category] = acc[t.category] || []).push(t);
      return acc;
    }, {}),
    [types],
  );
  // Flattened combobox options for the resolution-type picker.
  const typeOptions = useMemo(
    () => [
      // Custom / AI-drafted option always sits at the top of the dropdown
      // — this is the fallback when no catalogued template matches what the
      // user needs. Backend already accepts `resolution_type_key: "custom"`
      // and drafts via Claude using `custom_title` + `custom_context`.
      { value: "custom", label: "✨ Custom / AI-drafted resolution", group: "AI drafting" },
      ...Object.entries(grouped).flatMap(([cat, list]) =>
        list.map((t) => ({ value: t.key, label: t.title, group: cat })),
      ),
    ],
    [grouped],
  );

  // Members of the currently selected company — used to power the
  // MemberPicker dropdown inside SpecificFieldsPanel (transferor/transferee
  // name fields). Falls back to [] when no company picked.
  const selectedCompanyMembers = useMemo(() => {
    const c = companies.find((x) => x.id === form.company_id);
    return c?.members || [];
  }, [companies, form.company_id]);

  if (loading) return <div className="max-w-5xl mx-auto p-10 text-zinc-500">Loading…</div>;

  if (companies.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="no-companies-warn">
        <Building2 size={32} className="mx-auto text-zinc-400 mb-4" />
        <h1 className="font-heading text-3xl font-light mb-2">Add a company first</h1>
        <p className="text-zinc-600 mb-6">You&apos;ll need at least one company on record to generate resolutions.</p>
        <button onClick={() => navigate("/companies/new")} className="swiss-btn-primary" data-testid="add-company-from-generate">
          <Building2 size={14} /> Add Company
        </button>
      </div>
    );
  }

  // ============== PREVIEW MODE ==============
  if (draftBatch) {
    const active = editedDrafts[activeDraftIdx];
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="generate-preview">
        <button onClick={() => setDraftBatch(null)} className="text-sm text-zinc-500 hover:text-[#002FA7] mb-6 flex items-center gap-2" data-testid="back-to-form">
          <ArrowLeft size={14} /> Back to form
        </button>
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="label-eyebrow mb-2">Preview · Editable</div>
            <h1 className="font-heading text-4xl font-light tracking-tight">Review {editedDrafts.length} Draft{editedDrafts.length > 1 ? "s" : ""}</h1>
            <p className="text-zinc-600 text-sm mt-2">
              {draftBatch.company_name} · {draftBatch.meeting_type.toUpperCase()} on {draftBatch.meeting_date}
              {draftBatch.used_style_samples > 0 && (
                <span className="ml-2 text-[#002FA7]">· styled from {draftBatch.used_style_samples} sample{draftBatch.used_style_samples > 1 ? "s" : ""}</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setDraftBatch(null)} className="swiss-btn-ghost" data-testid="discard-drafts">
              <X size={14} /> Discard
            </button>
            <button onClick={saveAll} disabled={saving} className="swiss-btn-primary" data-testid="save-all">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Saving…" : `Save ${editedDrafts.length} Resolution${editedDrafts.length > 1 ? "s" : ""}`}
            </button>
          </div>
        </div>

        {/* Draft tabs */}
        {editedDrafts.length > 1 && (
          <div className="flex border-b border-zinc-200 mb-6 overflow-x-auto" data-testid="draft-tabs">
            {editedDrafts.map((d, i) => (
              <button
                key={d.draft_id}
                onClick={() => { setActiveDraftIdx(i); setActiveTab("resolution"); }}
                className={`px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${
                  activeDraftIdx === i ? "border-[#002FA7] text-[#002FA7] font-medium" : "border-transparent text-zinc-600 hover:text-zinc-900"
                }`}
                data-testid={`draft-tab-${i}`}
              >
                <span className="font-mono-plex text-xs mr-2">#{i + 1}</span>
                {d.resolution_title}
              </button>
            ))}
          </div>
        )}

        {/* Doc-type tabs within active draft */}
        <div className="flex border border-zinc-200 mb-4 w-fit" data-testid="doc-tabs">
          {[
            { key: "resolution", label: "Resolution", show: true },
            { key: "shareholder_resolution", label: "Shareholder Res.", show: !!active.shareholder_resolution_body },
            { key: "notice", label: "Notice", show: !!active.notice_body },
            { key: "minutes", label: "Minutes", show: !!active.minutes_body },
          ].filter((t) => t.show).map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2 text-sm border-r border-zinc-200 last:border-r-0 ${
                activeTab === t.key ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700"
              }`}
              data-testid={`doc-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Editable body */}
        <div className="border border-zinc-200 bg-white p-1" data-testid="draft-editor">
          <textarea
            value={
              activeTab === "resolution" ? active.resolution_body :
              activeTab === "notice" ? active.notice_body || "" :
              activeTab === "minutes" ? active.minutes_body || "" :
              active.shareholder_resolution_body || ""
            }
            onChange={(e) => {
              const field =
                activeTab === "resolution" ? "resolution_body" :
                activeTab === "notice" ? "notice_body" :
                activeTab === "minutes" ? "minutes_body" :
                "shareholder_resolution_body";
              updateDraftField(activeDraftIdx, field, e.target.value);
            }}
            rows={26}
            className="w-full p-5 text-sm font-mono-plex leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-none"
            data-testid="draft-body"
          />
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Editable Title">
            <input
              value={active.resolution_title}
              onChange={(e) => updateDraftField(activeDraftIdx, "resolution_title", e.target.value)}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              data-testid="draft-title"
            />
          </Field>
          {active.applicable_sections?.length > 0 && (
            <div>
              <span className="label-eyebrow block mb-1.5">Sections</span>
              <div className="text-xs text-zinc-700">{active.applicable_sections.join(" · ")}</div>
              {active.roc_forms?.length > 0 && (
                <div className="text-xs text-zinc-500 mt-1">ROC: {active.roc_forms.join(" · ")}</div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============== FORM MODE ==============
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="generate-page">
      <button onClick={() => navigate(-1)} className="text-sm text-zinc-500 hover:text-[#002FA7] mb-6 flex items-center gap-2" data-testid="back-button">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="label-eyebrow mb-2">Draft</div>
      <div className="flex items-start justify-between gap-4 mb-8">
        <h1 className="font-heading text-4xl font-light tracking-tight">Generate Resolutions</h1>
        <button
          onClick={() => navigate("/generate/multi")}
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2 border border-blue-700 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-50 transition"
          data-testid="multi-mode-cta"
          title="Same resolutions across many companies (quarterly batch)"
        >
          <Sparkles className="w-4 h-4" /> Multi-Company Mode
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-px bg-zinc-200 border border-zinc-200">
        {/* LEFT — form */}
        <div className="bg-white p-8 space-y-8">
          <Section icon={Building2} title="Company">
            <Combobox
              options={companies.map((c) => ({
                value: c.id,
                label: c.cin ? `${c.name} (${c.cin})` : c.name,
                group: c.company_type || "",
              }))}
              value={form.company_id}
              onChange={(v) => setForm({ ...form, company_id: v, signing_director_dins: [], meeting_place: "" })}
              placeholder="Search and pick a company…"
              testId="company-select"
              emptyLabel="No companies match. Try a different search."
            />
          </Section>

          {/* Resolutions — multi */}
          <Section icon={ScrollText} title={`Resolutions on Agenda (${items.length}/${MAX_ITEMS})`}>
            <div className="space-y-4">
              {items.map((it, idx) => {
                const rt = types.find((t) => t.key === it.resolution_type_key);
                return (
                  <div key={it._rid || `item-${idx}`} className="border border-zinc-200 p-4 bg-zinc-50/50" data-testid={`item-${idx}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <span className="label-eyebrow">Item #{idx + 1}</span>
                      {items.length > 1 && (
                        <button onClick={() => removeItem(idx)} className="text-zinc-400 hover:text-red-600" data-testid={`remove-item-${idx}`}>
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                    <Combobox
                      options={typeOptions}
                      value={it.resolution_type_key}
                      onChange={(v) => updateItem(idx, { resolution_type_key: v })}
                      placeholder="Search resolution / procedure…"
                      testId={`item-type-${idx}`}
                      emptyLabel="No procedures match. Try simpler keywords (e.g. 'auditor', 'banking')."
                    />
                    {/* keep a small spacer to preserve layout that used to be the select's margin */}
                    <div className="mb-2" />
                    {it.resolution_type_key === "custom" && (
                      <div className="border border-[#002FA7]/25 bg-[#002FA7]/[0.04] p-3 mb-3 space-y-2" data-testid={`custom-panel-${idx}`}>
                        <div className="text-[11px] uppercase tracking-[0.14em] text-[#002FA7] font-medium flex items-center gap-1.5">
                          <Sparkles size={12} /> AI-drafted resolution
                        </div>
                        <p className="text-[11px] text-zinc-600 leading-relaxed">
                          Describe what the board / shareholders need to approve. Claude will draft a
                          legally-worded resolution using this company&apos;s master data (name, CIN, address,
                          directors) and cite the applicable Companies Act sections.
                        </p>
                        <input
                          value={it.custom_title}
                          onChange={(e) => updateItem(idx, { custom_title: e.target.value })}
                          placeholder="Short title — e.g. 'Approve executive stock option grant to Ms Priya'"
                          className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white"
                          data-testid={`item-title-${idx}`}
                        />
                        <label className="flex items-center gap-2 text-[11px] text-zinc-700">
                          <input
                            type="checkbox"
                            checked={it.include_shareholder_resolution || false}
                            onChange={(e) => {
                              updateItem(idx, {
                                include_shareholder_resolution: e.target.checked,
                                is_special_resolution: e.target.checked ? it.is_special_resolution : false,
                              });
                              if (e.target.checked && form.meeting_type === "board") {
                                setForm((f) => ({ ...f, meeting_type: "egm" }));
                              }
                            }}
                            className="accent-[#002FA7]"
                            data-testid={`custom-shareholder-${idx}`}
                          />
                          <span>Also draft a shareholder resolution (needed if this matter goes to members)</span>
                        </label>
                        {it.include_shareholder_resolution && (
                          <label className="flex items-center gap-2 text-[11px] text-zinc-700 ml-5">
                            <input
                              type="checkbox"
                              checked={it.is_special_resolution || false}
                              onChange={(e) => updateItem(idx, { is_special_resolution: e.target.checked })}
                              className="accent-[#002FA7]"
                              data-testid={`custom-special-${idx}`}
                            />
                            <span>Special resolution (needs 75% majority)</span>
                          </label>
                        )}
                      </div>
                    )}
                    {rt?.specific_fields?.length > 0 && (
                      <SpecificFieldsPanel
                        fields={rt.specific_fields}
                        values={it.specific_fields}
                        onChange={(sf) => updateItem(idx, { specific_fields: sf })}
                        title={rt.title}
                        testIdPrefix={`specific-fields-${idx}`}
                        fieldTestIdPrefix={`specific-${idx}`}
                        members={selectedCompanyMembers}
                      />
                    )}
                    <textarea
                      value={it.custom_context}
                      onChange={(e) => updateItem(idx, { custom_context: e.target.value })}
                      placeholder={
                        it.resolution_type_key === "custom"
                          ? "Describe what needs to be approved. Include amounts, names of parties, effective dates, and any reference to prior BR/AGM decisions. Example:\n\n\"Approve grant of 50,000 stock options at ₹100/share to CTO Ms Priya Menon under the Company's ESOP-2024 scheme, vesting 25% annually over 4 years, exercise window 5 years from vest. Ratify Nomination & Remuneration Committee recommendation dated 12-Aug-2025. Note reg 22 SEBI compliance NA (private co).\""
                          : (rt?.specific_fields?.length > 0
                              ? "Optional — any extra background / one-off notes (transaction particulars already captured above)"
                              : "Context / event details (e.g. 'Open current account with HDFC, MG Road branch')")
                      }
                      rows={it.resolution_type_key === "custom" ? 6 : 2}
                      className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white"
                      data-testid={`item-context-${idx}`}
                    />
                    {rt && (rt.applicable_sections?.length > 0 || rt.resolution_kind) && (
                      <div className="flex items-center gap-2 mt-2 text-[10px] uppercase tracking-[0.12em] text-zinc-500 flex-wrap">
                        {rt.applicable_sections?.length > 0 && <span>Sec {rt.applicable_sections.slice(0, 2).join(", ")}</span>}
                        {rt.roc_forms?.length > 0 && <><span>·</span><span>{rt.roc_forms[0]}</span></>}
                        <span>·</span>
                        <span className={it.is_special_resolution ? "text-[#002FA7]" : ""}>{(rt.resolution_kind || "board").toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                );
              })}
              <button onClick={addItem} disabled={items.length >= MAX_ITEMS} className="swiss-btn-ghost w-full justify-center" data-testid="add-item">
                <Plus size={14} /> Add Another Resolution
              </button>
            </div>
          </Section>

          {/* Directors */}
          <Section icon={ScrollText} title="Signing Directors">
            {company && company.directors?.length > 0 ? (
              <div className="space-y-2" data-testid="directors-list">
                {company.directors.map((d, i) => {
                  const key = d.din || d.name;
                  const checked = form.signing_director_dins.includes(key);
                  return (
                    <label
                      key={key + i}
                      className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                        checked ? "border-[#002FA7] bg-[#002FA7]/5" : "border-zinc-200 hover:border-zinc-400"
                      }`}
                      data-testid={`director-checkbox-${i}`}
                    >
                      <input type="checkbox" checked={checked} onChange={() => toggleDirector(key)} className="accent-[#002FA7]" />
                      <div className="flex-1">
                        <div className="font-medium text-sm">{d.name}</div>
                        <div className="text-xs text-zinc-500 font-mono-plex">DIN: {d.din || "—"} · {d.designation}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-zinc-500">No directors on record. Add them in the company page first.</p>
            )}
          </Section>

          {/* Meeting */}
          <Section icon={Calendar} title="Meeting">
            <div className="mb-4">
              <span className="label-eyebrow block mb-2">Meeting Type</span>
              <div className="flex border border-zinc-300 w-fit" data-testid="meeting-type-tabs">
                {[
                  { k: "board", l: "Board" },
                  { k: "agm", l: "AGM" },
                  { k: "egm", l: "EGM" },
                ].map((opt) => (
                  <button
                    key={opt.k}
                    type="button"
                    onClick={() => setForm({ ...form, meeting_type: opt.k })}
                    className={`px-4 py-2 text-sm font-medium border-r border-zinc-300 last:border-r-0 ${
                      form.meeting_type === opt.k ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700"
                    }`}
                    data-testid={`meeting-type-${opt.k}`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
              {form.meeting_type !== "board" && (
                <p className="text-[11px] text-zinc-500 mt-2">Notice will be drafted under SS-2 with 21 clear days, addressed to all members.</p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Date">
                <input type="date" value={form.meeting_date} onChange={(e) => setForm({ ...form, meeting_date: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="meeting-date" />
              </Field>
              <Field label="Time">
                <input type="text" value={form.meeting_time} onChange={(e) => setForm({ ...form, meeting_time: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="meeting-time" />
              </Field>
              <div className="md:col-span-2">
                <Field label="Venue">
                  <input type="text" value={form.meeting_place} onChange={(e) => setForm({ ...form, meeting_place: e.target.value })} placeholder="Registered office or alternate venue" className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="meeting-place" />
                </Field>
              </div>
            </div>
          </Section>

          <Section title="Output">
            <div className="space-y-3">
              <Toggle label="Generate Notice of Meeting" checked={form.include_notice} onChange={(v) => setForm({ ...form, include_notice: v })} testId="toggle-notice" />
              <Toggle label="Generate Minutes of Meeting" checked={form.include_minutes} onChange={(v) => setForm({ ...form, include_minutes: v })} testId="toggle-minutes" />
              <Toggle label="Use Style Library (mirror house style)" checked={form.use_style_library} onChange={(v) => setForm({ ...form, use_style_library: v })} testId="toggle-style-library" />
            </div>
          </Section>

          <button onClick={generate} disabled={generating} className="swiss-btn-primary w-full justify-center py-3 text-base" data-testid="generate-button">
            {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {generating ? `Drafting ${items.length} resolution${items.length > 1 ? "s" : ""}…` : `Generate ${items.length} Draft${items.length > 1 ? "s" : ""}`}
            {!generating && <ArrowRight size={16} />}
          </button>
        </div>

        {/* RIGHT — summary */}
        <div className="bg-zinc-50 p-8">
          <div className="label-eyebrow mb-6">Meeting Summary</div>
          <SummaryRow k="Company" v={company?.name || "—"} />
          <SummaryRow k="Meeting Type" v={form.meeting_type.toUpperCase()} />
          <SummaryRow k="Meeting Date" v={form.meeting_date} />
          <SummaryRow k="Meeting Time" v={form.meeting_time} />
          <SummaryRow k="Resolutions" v={`${items.length} item${items.length > 1 ? "s" : ""}`} />
          <SummaryRow k="Signatories" v={form.signing_director_dins.length > 0 ? `${form.signing_director_dins.length} selected` : "None"} />
          <SummaryRow k="Style Library" v={form.use_style_library ? "Active" : "Off"} />

          <div className="mt-8 p-4 border border-zinc-300 bg-white">
            <div className="label-eyebrow mb-2">Numbering</div>
            <p className="text-sm text-zinc-700">
              Each resolution will be numbered sequentially as <span className="font-mono-plex font-medium">BR/&lt;FY&gt;/&lt;NN&gt;</span> (assigned only on Save).
            </p>
          </div>

          <div className="mt-4 p-4 border border-[#002FA7]/30 bg-[#002FA7]/5">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen size={14} className="text-[#002FA7]" />
              <span className="label-eyebrow text-[#002FA7]">Edit-before-save</span>
            </div>
            <p className="text-xs text-zinc-700 leading-relaxed">
              You&apos;ll see a preview of all drafts with editable text. Tweak anything, then click <strong>Save</strong> to persist with shared meeting numbering.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={14} strokeWidth={1.5} className="text-[#002FA7]" />}
        <span className="label-eyebrow">{title}</span>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function SummaryRow({ k, v }) {
  return (
    <div className="flex justify-between border-b border-zinc-200 py-2 text-sm">
      <span className="text-zinc-500">{k}</span>
      <span className="font-medium text-right max-w-[60%] truncate">{v}</span>
    </div>
  );
}

function Toggle({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center justify-between cursor-pointer" data-testid={testId}>
      <span className="text-sm">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`w-10 h-5 border transition-colors flex items-center ${checked ? "bg-[#002FA7] border-[#002FA7]" : "bg-white border-zinc-400"}`}
        aria-pressed={checked}
      >
        <span className={`block w-4 h-4 transition-transform ${checked ? "bg-white translate-x-5" : "bg-zinc-500 translate-x-0.5"}`} />
      </button>
    </label>
  );
}
